"use client"

import Text from "@/components/common/Typography/Text"
import Title from "@/components/common/Typography/Title"
import { useTERouter } from "@/hooks/app/useTERouter"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useAirports } from "@/hooks/flight/useAirports"
import { flightActions } from "@/lib/redux/features/flight/reduce"
import { initialStateFlight } from "@/lib/redux/features/flight/initialState"
import { useAppDispatch } from "@/lib/redux/hooks"
import { Button, Card, Col, Row } from "antd"
import dayjs from "dayjs"
import { useEffect, useMemo, useState } from "react"

type LandingScheduleProps = {
  code?: string
  flights?: any[]
  routeMeta?: any
  onBookNow?: () => void
}

const getDateValue = (item: any) =>
  item?.date || item?.startDate || item?.departDate || item?.departureDate

const getPriceValue = (item: any) =>
  // item?.lowestFare || item?.price || item?.minPrice || item?.amount || item?.totalPrice
  item?.lowestPriceTotal

const formatPrice = (value: any) => {
  if (value === null || value === undefined) return "—"
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return "—"
  return new Intl.NumberFormat("vi-VN").format(numeric)
}

const formatPriceCompact = (value: any) => {
  if (value === null || value === undefined) return "—"
  const numeric = Number(value)
  if (Number.isNaN(numeric)) return "—"
  if (numeric >= 1_000_000) {
    return `${(numeric / 1_000_000).toFixed(2)}M`
  }
  if (numeric >= 1_000) {
    return `${(numeric / 1_000).toFixed(2)}K`
  }
  return String(Math.round(numeric))
}

const normalizeAirlineCode = (value?: string) => {
  if (!value) return ""
  return String(value).trim().toUpperCase()
}

const resolveAirlineInfo = (airlineCode?: string) => {
  const normalized = normalizeAirlineCode(airlineCode)
  const normalizedNoUnderscore = normalized.replace(/^_+/, "")

  const airlineData = (initialStateFlight as any)?.airlineData || {}
  const fromMap = airlineData?.[normalized] || airlineData?.[normalizedNoUnderscore]
  if (fromMap) {
    return {
      name: fromMap?.name as string | undefined,
      logo: (fromMap?.url) as string | undefined,
    }
  }

  const airlines = Array.isArray((initialStateFlight as any)?.airlines)
    ? ((initialStateFlight as any).airlines as any[])
    : []
  const fromList =
    airlines.find((a) => normalizeAirlineCode(a?.code) === normalized) ||
    airlines.find((a) => normalizeAirlineCode(a?.code) === normalizedNoUnderscore)

  return {
    name: fromList?.name as string | undefined,
    logo: fromList?.logo as string | undefined,
  }
}

const resolveAirlineInfoSmallScreen = (airlineCode?: string) => {
  const normalized = normalizeAirlineCode(airlineCode)
  const normalizedNoUnderscore = normalized.replace(/^_+/, "")

  const airlineData = (initialStateFlight as any)?.airlines || {}
  const fromMap = airlineData?.[normalized] || airlineData?.[normalizedNoUnderscore]
  if (fromMap) {
    return {
      name: fromMap?.name as string | undefined,
      logo: (fromMap?.url) as string | undefined,
    }
  }

  const airlines = Array.isArray((initialStateFlight as any)?.airlines)
    ? ((initialStateFlight as any).airlines as any[])
    : []
  const fromList =
    airlines.find((a) => normalizeAirlineCode(a?.code) === normalized) ||
    airlines.find((a) => normalizeAirlineCode(a?.code) === normalizedNoUnderscore)

  return {
    name: fromList?.name as string | undefined,
    logo: fromList?.logo as string | undefined,
  }
}


const normalizeImageUrl = (value?: string) => {
  if (!value) return ""
  const trimmed = String(value).trim()
  if (!trimmed) return ""
  if (trimmed.startsWith("http://")) return `https://${trimmed.slice("http://".length)}`
  if (trimmed.startsWith("//")) return `https:${trimmed}`
  return trimmed
}

const toRem = (px: number) => `${(px / 9).toFixed(3)}rem`

type FlightSearchInput = {
  startPoint?: string
  endPoint?: string
  date?: any
}

// Build a search string compatible with flights result page (similar to landing.Flight flow)
// Fixed params: adult=1, child=0, infant=0, itinerary=0, class=
const buildFlightSearchQueryString = ({ startPoint, endPoint, date }: FlightSearchInput) => {
  const params = new URLSearchParams()
  params.set("adult", "1")
  params.set("child", "0")
  params.set("infant", "0")
  params.set("itinerary", "0")

  params.set("t1", endPoint ? String(endPoint).trim() : "")
  params.set("f1", startPoint ? String(startPoint).trim() : "")

  const dateValue = date ? dayjs(date) : dayjs()
  params.set("df1", (dateValue.isValid() ? dateValue : dayjs()).format("YYYY-MM-DD"))

  params.set("class", "")
  return params.toString()
}

const LandingSchedule = ({ code, flights = [], routeMeta = {}, onBookNow }: LandingScheduleProps) => {
  const normalizedFlights = Array.isArray(flights) ? flights : []
  const airports = useAirports()
  const { getAirportCityName } = useTETranslation()
  const dispatch = useAppDispatch()
  const router = useTERouter()

  const [isSmallScreen, setIsSmallScreen] = useState(false)
  useEffect(() => {
    if (typeof window === "undefined") return
    const mq = window.matchMedia("(max-width: 767.98px)")
    const onChange = () => setIsSmallScreen(!!mq.matches)
    onChange()
    mq.addEventListener?.("change", onChange)
    return () => mq.removeEventListener?.("change", onChange)
  }, [])

  const routeMetaObj = Array.isArray(routeMeta) ? routeMeta?.[0] : routeMeta
  const fromName =
    routeMetaObj?.startPoint ||
    routeMetaObj?.fromName ||
    routeMetaObj?.from ||
    routeMetaObj?.fromAirport ||
    "—"
  const toName =
    routeMetaObj?.endPoint ||
    routeMetaObj?.toName ||
    routeMetaObj?.to ||
    routeMetaObj?.toAirport ||
    "—"

  const handleBookNow = () => {
    onBookNow?.()
  }

  const getStartPointAny = (item: any) =>
    item?.startPoint ||
    item?.from ||
    item?.fromAirport ||
    item?.origin ||
    item?.fromCode ||
    item?.productMetaData?.startPoint ||
    routeMetaObj?.startPoint ||
    routeMetaObj?.from ||
    routeMetaObj?.fromCode ||
    routeMetaObj?.fromAirport

  const getEndPointAny = (item: any) =>
    item?.endPoint ||
    item?.to ||
    item?.toAirport ||
    item?.destination ||
    item?.toCode ||
    item?.productMetaData?.endPoint ||
    routeMetaObj?.endPoint ||
    routeMetaObj?.to ||
    routeMetaObj?.toCode ||
    routeMetaObj?.toAirport

  const getDepartDateAny = (item: any) =>
    item?.departureDate || item?.departDate || item?.date || item?.startDate || item?.departDate || item?.departDate

  const runQuickSearch = ({ startPoint, endPoint, date }: FlightSearchInput) => {
    const startCode = startPoint ? String(startPoint).trim() : ""
    const endCode = endPoint ? String(endPoint).trim() : ""
    if (!startCode || !endCode) {
      handleBookNow()
      return
    }

    const dateValue = date ? dayjs(date) : dayjs()
    const dateFrom = (dateValue.isValid() ? dateValue : dayjs()).format("YYYY-MM-DD")

    // Sync redux flight search state similar to landing flight search
    dispatch(flightActions.updateModeSearchFlight({ isMultiCities: false }))
    dispatch(flightActions.changeCheckedItinerary(false))
    dispatch(flightActions.inputAdult(1))
    dispatch(flightActions.inputChildren(0))
    dispatch(flightActions.inputInfant(0))
    dispatch(flightActions.selectCabinClass(""))

    const fromAirport = (airports as any)?.[startCode]
    const toAirport = (airports as any)?.[endCode]
    if (fromAirport) dispatch(flightActions.selectFromAirport({ indexInputFlight: 0, fromAirport }))
    if (toAirport) dispatch(flightActions.selectToAirport({ indexInputFlight: 0, toAirport }))
    dispatch(flightActions.changeDateFrom({ indexInputFlight: 0, dateFrom }))

    const qs = buildFlightSearchQueryString({ startPoint: startCode, endPoint: endCode, date: dateFrom })
    router.push(`/flights/result?${qs}`)
  }

  if (code === "ITIN_ROUTE") {
    const itin = useMemo(() => {
      const baseDate = getDateValue(normalizedFlights[0]) || dayjs().valueOf()
      const month = dayjs(baseDate).startOf("month")
      // Always render week starting on Sunday (CN) to match the header labels.
      // Do NOT depend on locale-based week start (e.g. vi-VN often starts on Monday).
      const monthStart = month.startOf("month")
      const monthEnd = month.endOf("month")
      const start = monthStart.subtract(monthStart.day(), "day")
      const end = monthEnd.add(6 - monthEnd.day(), "day")
      const days = Array.from({ length: end.diff(start, "day") + 1 }, (_, idx) => start.add(idx, "day"))

      const flightByDate = normalizedFlights.reduce((acc: Record<string, any>, item: any) => {
        const dateValue = getDateValue(item)
        if (!dateValue) return acc
        acc[dayjs(dateValue).format("YYYY-MM-DD")] = item
        return acc
      }, {})

      const weeks: Array<typeof days> = []
      for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7))
      }

      const today = dayjs().startOf("day")
      const currentWeekIndex = Math.max(0, weeks.findIndex((w) => w.some((d) => d.isSame(today, "day"))))

      return { month, days, weeks, flightByDate, currentWeekIndex }
    }, [normalizedFlights])

    const [weekIndex, setWeekIndex] = useState(0)
    useEffect(() => {
      if (!isSmallScreen) return
      setWeekIndex(itin.currentWeekIndex)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSmallScreen, itin.currentWeekIndex])

    const safeWeekIndex = Math.min(Math.max(0, weekIndex), Math.max(0, itin.weeks.length - 1))
    const shownDays = isSmallScreen ? (itin.weeks[safeWeekIndex] || itin.weeks[0] || []) : itin.days
    const weekRangeLabel = isSmallScreen && (itin.weeks[safeWeekIndex]?.length)
      ? `${itin.weeks[safeWeekIndex][0].format("DD/MM")} - ${itin.weeks[safeWeekIndex][itin.weeks[safeWeekIndex].length - 1].format("DD/MM")}`
      : ""

    return (
      <Row className="landing-schedule" style={{ margin: 0, maxWidth: "100%" }}>
        <Col span={24}>
          <div
            style={{
              borderRadius: toRem(16),
              padding: toRem(16),
              background: "rgba(255,255,255,0.86)",
              border: "0.111rem solid rgba(15,23,42,0.10)",
              boxShadow: "0 1.111rem 3.333rem rgba(15,23,42,0.10)",
              backdropFilter: "blur(1.111rem)",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: toRem(12),
                flexWrap: "wrap",
                marginBottom: toRem(12),
              }}
            >
              <div style={{ minWidth: toRem(240) }}>
                <div style={{ display: "flex", alignItems: "center", gap: toRem(8), flexWrap: "wrap" }}>
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: toRem(6),
                      padding: `${toRem(6)} ${toRem(10)}`,
                      borderRadius: toRem(999),
                      background: "rgba(62,181,232,0.16)",
                      border: "0.111rem solid rgba(62,181,232,0.38)",
                      color: "#0b4b63",
                      fontWeight: 600,
                      fontSize: "1.4rem",
                      lineHeight: "1.6rem",
                    }}
                  >
                    Hành trình: {fromName} → {toName}
                  </span>
                  <Text type="secondary" className="te-text-small">
                    Tháng {itin.month.format("M")}/{itin.month.format("YYYY")}
                  </Text>
                </div>

                <Title level={2} style={{ margin: `${toRem(10)} 0 0` }}>
                  Lịch giá vé theo tháng {itin.month.format("M")}/{itin.month.format("YYYY")}
                </Title>
                {/* <Text style={{ color: "rgba(0,0,0,0.60)", fontSize: 13 }}>
                  Giá hiển thị là mức rẻ nhất trong ngày.
                </Text> */}
              </div>

              {/* <Button
                type="primary"
                size="large"
                onClick={handleBookNow}
                style={{
                  borderRadius: 999,
                  height: 44,
                  paddingInline: 18,
                  fontWeight: 800,
                  border: "none",
                  background: "linear-gradient(135deg, #3EB5E8 0%, #1677ff 100%)",
                    boxShadow: "0 1.111rem 2.222rem rgba(22,119,255,0.25)",
                }}
              >
                Đặt ngay
              </Button> */}
            </div>

            <div
              style={{
                borderRadius: toRem(12),
                padding: toRem(12),
                background: "rgba(255,255,255,0.75)",
                border: "0.111rem solid rgba(15,23,42,0.08)",
                maxWidth: "100%",
              }}
            >
              {isSmallScreen && itin.weeks.length > 1 ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: toRem(12),
                    marginBottom: toRem(10),
                  }}
                >
                  <Button
                    size="small"
                    onClick={() => setWeekIndex((v) => Math.max(0, v - 1))}
                    disabled={safeWeekIndex <= 0}
                  >
                    Trước
                  </Button>
                  <Text type="secondary" className="te-text-small" style={{ textAlign: "center" }}>
                    Tuần {safeWeekIndex + 1}/{itin.weeks.length}{weekRangeLabel ? ` (${weekRangeLabel})` : ""}
                  </Text>
                  <Button
                    size="small"
                    onClick={() => setWeekIndex((v) => Math.min(itin.weeks.length - 1, v + 1))}
                    disabled={safeWeekIndex >= itin.weeks.length - 1}
                  >
                    Sau
                  </Button>
                </div>
              ) : null}

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                  gap: toRem(10),
                  maxWidth: "100%",
                }}
              >
                {["CN", "T2", "T3", "T4", "T5", "T6", "T7"].map((d) => (
                  <div
                    key={d}
                    style={{
                      fontWeight: 800,
                      fontSize: toRem(12),
                      textAlign: "center",
                      color: "rgba(0,0,0,0.65)",
                      paddingBlock: toRem(6),
                      minWidth: 0,
                    }}
                  >
                    {d}
                  </div>
                ))}

                {shownDays.map((d) => {
                  const key = d.format("YYYY-MM-DD")
                  const item = itin.flightByDate[key]
                  const inMonth = d.month() === itin.month.month()
                  const priceValue = item ? getPriceValue(item) : null
                  const priceLabel = isSmallScreen
                    ? formatPriceCompact(priceValue)
                    : `${formatPrice(priceValue)}₫`
                  const airlineCode = item?.airlineCode
                  const airlineInfo = airlineCode ? resolveAirlineInfo(airlineCode) : null
                  const airlineName = airlineInfo?.name || (airlineCode ? normalizeAirlineCode(airlineCode) : "")
                  const airlineLogo = airlineInfo?.logo

                  return (
                    <div
                      key={key}
                      className={`landing-schedule__day${item && inMonth ? " is-clickable" : ""}`}
                      style={{
                        padding: isSmallScreen ? `${toRem(6)} ${toRem(6)}` : `${toRem(10)} ${toRem(10)}`,
                        minHeight: isSmallScreen ? toRem(64) : toRem(82),
                        borderRadius: toRem(12),
                        border: inMonth
                          ? "0.111rem solid rgba(15,23,42,0.10)"
                          : "0.111rem dashed rgba(15,23,42,0.14)",
                        background: inMonth ? "rgba(255,255,255,0.96)" : "rgba(15,23,42,0.04)",
                        opacity: inMonth ? 1 : 0.6,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        cursor: item && inMonth ? "pointer" : "default",
                        minWidth: 0,
                        overflow: "hidden",
                      }}
                      role={item && inMonth ? "button" : undefined}
                      tabIndex={item && inMonth ? 0 : -1}
                      onClick={() => {
                        if (!item || !inMonth) return
                        runQuickSearch({
                          startPoint: getStartPointAny(item),
                          endPoint: getEndPointAny(item),
                          date: getDepartDateAny(item) || d.format("YYYY-MM-DD"),
                        })
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Text
                          className="te-text-small"
                          style={{
                            fontWeight: 600,
                            fontSize: isSmallScreen ? toRem(14) : toRem(16),
                            lineHeight: toRem(14),
                            color: "rgba(0, 0, 0, 0.65)",
                            flexShrink: 0,
                            minWidth: "2ch",
                            whiteSpace: "nowrap",
                            overflowWrap: "normal",
                            wordBreak: "keep-all",
                          }}
                          strong
                        >
                          {d.date()}
                        </Text>
                        {airlineName && !isSmallScreen ? (
                          <Text
                            title={airlineName}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: toRem(6),
                              flex: 1,
                              minWidth: 0,
                              maxWidth: toRem(150),
                              fontWeight: 600,
                              color: "rgba(0,0,0,0.55)",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {airlineLogo ? (
                              <img
                                src= {normalizeImageUrl(airlineLogo)}
                                alt={airlineName}
                                title={airlineName}
                                width={isSmallScreen ? 15 : 50}
                                height={isSmallScreen ? 5 : 25}
                                style={{
                                  display: "block",
                                  borderRadius: toRem(6),
                                  objectFit: "contain",
                                  marginLeft: "auto",
                                }}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none"
                                }}
                              />
                            ) : null}
                          </Text>
                        ) : null}
                      </div>

                      {item ? (
                        <Text
                          strong
                          style={{
                            fontSize: isSmallScreen ? toRem(8) : toRem(12),
                            lineHeight: toRem(14),
                            whiteSpace: "nowrap",
                            wordBreak: "keep-all",
                          }}
                        >
                          {priceLabel}
                        </Text>
                      ) : (
                        <Text type="secondary" className="te-text-small">
                          —
                        </Text>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </Col>
      </Row>
    )
  }

  if (code === "DES_ROUTE") {
    const getStartPoint = (item: any) =>
      item?.startPoint ||
      item?.from ||
      item?.fromAirport ||
      item?.origin ||
      item?.fromCode ||
      item?.productMetaData?.startPoint

    const getEndPoint = (item: any) =>
      item?.endPoint ||
      item?.to ||
      item?.toAirport ||
      item?.destination ||
      item?.toCode ||
      item?.productMetaData?.endPoint

    const getDepartDate = (item: any) => item?.departureDate || item?.departDate || item?.date || item?.startDate

    const buildTitle = (startCode?: string, endCode?: string) => {
      const fromAirport = startCode ? (airports as any)?.[startCode] : null
      const toAirport = endCode ? (airports as any)?.[endCode] : null

      const fromText = fromAirport ? getAirportCityName(fromAirport, { shownCode: true }) : (startCode ? `${startCode}` : "—")
      const toText = toAirport ? getAirportCityName(toAirport, { shownCode: true }) : (endCode ? `${endCode}` : "—")
      return { fromText, toText }
    }

    // DES_ROUTE: build cards directly from `flights` (do not use routeMetaData for titles)
    const cards = normalizedFlights
      .map((item: any) => {
        const startPoint = getStartPoint(item)
        const endPoint = getEndPoint(item)
        const departDate = getDepartDate(item)

        return {
          startPoint,
          endPoint,
          departDate,
          lowestFare: getPriceValue(item),
          airlineCode: item?.airlineCode,
          meta: item,
        }
      })
      .filter((item: any) => {
        const departDate = item?.departDate
        if (!departDate) return true
        const today = dayjs().startOf("day")
        const flightDate = dayjs(departDate).startOf("day")
        return flightDate.isSame(today, "day") || flightDate.isAfter(today, "day")
      })
      .slice(0, 8)

    return (
      <Row className="landing-schedule" style={{ margin: 0, maxWidth: "100%" }}>
        <Col span={24}>
          <div
            style={{
              borderRadius: toRem(16),
              padding: toRem(16),
              background: "rgba(255,255,255,0.86)",
              border: "0.111rem solid rgba(15,23,42,0.10)",
              boxShadow: "0 1.111rem 3.333rem rgba(15,23,42,0.10)",
              backdropFilter: "blur(1.111rem)",
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <Title
              level={3}
              style={{
                margin: `0 0 ${toRem(16)}`,
              }}
            >
              Chặng bay phổ biến
            </Title>

            {cards.length > 0 ? (
              <Row gutter={[16, 16]}>
                {cards.map((item: any, index: number) => {
                const startPoint = item?.startPoint
                const endPoint = item?.endPoint
                const { fromText, toText } = buildTitle(startPoint, endPoint)
                const departDate = item?.departDate || item?.departureDate || item?.date
                const price = item?.lowestFare
                const formattedDate = departDate ? dayjs(departDate).format("DD/MM/YYYY") : "—"

                const airlineCode = item?.airlineCode
                const airlineInfo = airlineCode ? resolveAirlineInfo(airlineCode) : null
                const airlineName = airlineInfo?.name || (airlineCode ? normalizeAirlineCode(airlineCode) : "")
                const airlineLogo = airlineInfo?.logo

                return (
                  <Col key={`${startPoint || "_"}-${endPoint || "_"}-${formattedDate}-${index}`} xs={24} sm={12} md={8} lg={8} xl={8}>
                    <Card
                      hoverable
                      className="landing-schedule__card"
                      style={{
                        borderRadius: toRem(14),
                        width: "100%",
                        maxWidth: toRem(390),
                        overflow: "hidden",
                        border: "0.111rem solid rgba(15,23,42,0.08)",
                      }}
                      styles={{
                        body: {
                          padding: toRem(16),
                          display: "flex",
                          flexDirection: "column",
                          gap: toRem(12),
                          minHeight: toRem(236),
                        },
                      }}
                    >
                <div
                  style={{
                    borderRadius: toRem(12),
                    padding: toRem(12),
                    background:
                      "linear-gradient(135deg, rgba(62,181,232,0.14) 0%, rgba(222, 235, 113, 0.14) 100%)",
                    border: "0.111rem solid rgba(15, 42, 33, 0.08)",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: toRem(10) }}>
                    <div style={{ minWidth: 0 }}>
                      <Text
                        strong
                        style={{
                          display: "block",
                          margin: 0,
                          lineHeight: toRem(18),
                          color: "rgba(0,0,0,0.88)",
                        }}
                      >
                        {fromText}
                      </Text>
                      <Text
                        strong
                        style={{
                          display: "block",
                          margin: 0,
                          lineHeight: toRem(18),
                          color: "rgba(0,0,0,0.88)",
                        }}
                      >
                        {toText}
                      </Text>
                      <Text type="secondary" className="te-text-small" style={{ display: "block", marginTop: toRem(4) }}>
                        Khởi hành: {formattedDate}
                      </Text>
                    </div>

                    {airlineLogo ? (
                      <img
                        src={normalizeImageUrl(airlineLogo)}
                        alt={airlineName || "airline"}
                        title={airlineName || airlineCode}
                        width={80}
                        height={50}
                        style={{
                          display: "block",
                          borderRadius: toRem(10),
                          // border: "1px solid rgba(15,23,42,0.08)",
                          objectFit: "contain",
                          flex: "0 0 auto",
                          marginLeft: "auto",
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none"
                        }}
                      />
                    ) : null}
                  </div>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: toRem(10), flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: toRem(10) }}>
                    <div>
                      <Text type="secondary" className="te-text-small" style={{ display: "block" }}>
                        Giá chỉ từ
                      </Text>
                      <Text strong style={{ display: "block" }}>
                        {formatPrice(price)} VND
                      </Text>
                    </div>
                    <Text type="secondary" className="te-text-small">
                      Eco / 1 Một chiều
                    </Text>
                  </div>

                  {/* <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                    <Text type="secondary" className="te-text-small">
                      Điều kiện áp dụng
                    </Text>
                    {airlineName ? (
                      <Text
                        title={airlineName}
                        type="secondary"
                        className="te-text-small"
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 6,
                        }}
                      >
                        {airlineCode ? normalizeAirlineCode(airlineCode) : airlineName}
                      </Text>
                    ) : null} */}
                  {/* </div> */}
                </div>

                <Button
                  block
                  onClick={() =>
                    runQuickSearch({
                      startPoint,
                      endPoint,
                      date: departDate,
                    })
                  }
                  style={{
                    marginTop: "auto",
                    height: toRem(40),
                    borderRadius: toRem(10),
                    fontWeight: 600,
                    border: "0.111rem solid rgba(0,0,0,0.06)",
                    background: "linear-gradient(180deg, #FFD54F 0%, #FFC107 100%)",
                    color: "#1f2937",
                  }}
                  className="landing-schedule__book-now-button"
                >
                  Đặt ngay
                </Button>
              </Card>
            </Col>
          )
        })}
            </Row>
            ) : (
              <Text style={{ fontSize: toRem(14), color: "rgba(0,0,0,0.65)" }}>
                Lịch bay đang được cập nhập, vui lòng quay lại sau...
              </Text>
            )}
          </div>
        </Col>
      </Row>
    )
  }

  return <></>
}

export default LandingSchedule
